import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Popconfirm, Row, Table, TableColumnsType, TableProps, Typography } from "antd";
import type { SorterResult } from "antd/lib/table/interface";
import axios from "axios";
import { SearchBar } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { CompanyUser } from "@bindeleddet/core/types";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

const AdminCompaniesList = () => {
  const { Title } = Typography;
  const history = useHistory();

  const { width } = useViewport();

  const [companies, setCompanies] = useState<CompanyUser[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchCompanies = (page = 1, pageSize = 10, search = "", ordering = "") => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (search) {
      params.append("search", search);
    }

    if (ordering) {
      params.append("ordering", ordering);
    }

    axios
      .get<{ results: CompanyUser[]; count: number }>(`/companies/admin-list/?${params.toString()}`)
      .then(response => {
        setCompanies(response.data.results);
        setPagination(prev => ({
          ...prev,
          total: response.data.count,
        }));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchCompanies(1, pagination.pageSize, "", "name");
  }, [pagination.pageSize]);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    fetchCompanies(1, pagination.pageSize, newSearchTerm);
  };

  const onRowClick = (companyID: number) => ({
    onClick: () => {
      history.push(`/admin/companies/${companyID}`);
    },
    style: {
      cursor: "pointer",
    },
  });

  type OnChange = NonNullable<TableProps<CompanyUser>["onChange"]>;
  type Sorts = SorterResult<CompanyUser>;

  const [sortedInfo, setSortedInfo] = useState<Sorts>({
    columnKey: "name",
    field: "name",
    order: "ascend",
  } as Sorts);

  const handleChange: OnChange = (tablePagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
    const ordering =
      sorter && !Array.isArray(sorter) && sorter.order ? `${sorter.order === "descend" ? "-" : ""}${sorter.field}` : "";

    setPagination(prev => ({
      ...prev,
      current: tablePagination.current || 1,
      pageSize: tablePagination.pageSize || 10,
    }));

    fetchCompanies(tablePagination.current || 1, tablePagination.pageSize || 10, searchTerm, ordering);
  };

  const columns: TableColumnsType<CompanyUser> = [
    {
      title: "Bedriftsnavn",
      key: "name",
      dataIndex: "name",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "name" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Vises",
      dataIndex: "is_relevant",
      key: "is_relevant",
      ellipsis: true,
      width: 200,
      render: (text, record) => {
        return record.is_relevant ? "Ja" : "Nei";
      },
    },
    {
      key: "x",
      render: (text, record) => {
        return (
          <Popconfirm
            key="delete-company"
            title={`Slett "${record.name}"`}
            okText="Slett"
            placement="top"
            cancelText="Avbryt"
            style={{ backgroundColor: "var(--neutral-alt)" }}
            okType="danger"
            onConfirm={e => {
              e?.stopPropagation();
              axios
                .delete<CompanyUser>(`/companies/${record.id}/`)
                .then(() => {
                  message("success", "Bedrift slettet");
                  fetchCompanies(pagination.current, pagination.pageSize, searchTerm);
                })
                .catch(() => message("error", "Kunne ikke slette bedrift. Prøv igjen senere."));
            }}
            onCancel={e => e?.stopPropagation()}
          >
            <DeleteOutlined
              style={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={event => event.stopPropagation()}
            />
          </Popconfirm>
        );
      },
      width: 50,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2} style={{ marginRight: "10px" }}>
            Bedrifter
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/admin/create-company-profile/")}>
            Opprett ny bedrift
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>
        {width > 768 ? (
          <Table
            columns={columns}
            dataSource={companies}
            onChange={handleChange}
            onRow={company => onRowClick(company.id)}
            loading={loading}
            pagination={pagination}
          />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Bedriftsnavn",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Vises",
                dataIndex: "is_relevant",
                key: "is_relevant",
                render: value => (value ? "Ja" : "Nei"),
              },
            ]}
            onClick={id => history.push(`/admin/companies/${id}/`)}
            data={companies}
            loading={loading}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize,
                }));
                fetchCompanies(page, pageSize, searchTerm);
              },
            }}
            emptyText="Ingen bedrifter funnet"
          />
        )}
      </Card>
    </>
  );
};

export default AdminCompaniesList;
